import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { validatePhotoUpload, generatePhotoUrls } from '../lib/utils'
import type {
  WeddingPhotoRow,
  PhotoUploadResponse,
  PhotoForDisplay,
  GalleryResponse,
  GalleryQueryParams,
} from '../lib/types'

/**
 * Custom hook for managing wedding photos
 * Handles upload, fetch, delete operations for both guest and admin users
 */
export function usePhotos() {
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(false)

  /**
   * Check if guest has reached photo limit
   */
  const checkPhotoLimit = useCallback(async (guestId: string): Promise<number> => {
    try {
      const { data, error } = await supabase.rpc('count_guest_photos', {
        p_guest_id: guestId,
      })

      if (error) {
        console.error('Error counting photos:', error)
        return 0
      }

      return data ?? 0
    } catch (error) {
      console.error('Error checking photo limit:', error)
      return 0
    }
  }, [])

  /**
   * Upload a photo for a guest
   * @param guestId - ID of the guest uploading the photo
   * @param file - The photo file to upload
   * @param guestPin - PIN of the guest (required for RLS policy)
   * @returns Upload response with success status and photo ID
   */
  const uploadPhoto = useCallback(
    async (guestId: string, file: File, guestPin?: string): Promise<PhotoUploadResponse> => {
      try {
        setUploading(true)

        // Check current photo count
        const currentCount = await checkPhotoLimit(guestId)

        // Validate file
        const validationError = validatePhotoUpload(file, currentCount)
        if (validationError) {
          return { success: false, error: validationError }
        }

        // Generate unique storage path
        const photoId = crypto.randomUUID()
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg'
        const storagePath = `${guestId}/${photoId}.${fileExtension}`

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('wedding-photos')
          .upload(storagePath, file, {
            cacheControl: '3600',
            upsert: false,
          })

        if (uploadError) {
          console.error('Storage upload error:', uploadError)

          // Provide helpful error message for missing bucket
          if (
            uploadError.message?.includes('Bucket not found') ||
            uploadError.message?.includes('bucket')
          ) {
            return {
              success: false,
              error:
                'Der Speicher-Bucket wurde nicht gefunden. Bitte wenden Sie sich an den Administrator.',
            }
          }

          return {
            success: false,
            error: 'Fehler beim Hochladen. Bitte versuchen Sie es erneut.',
          }
        }

        // Save metadata to database using function (validates PIN)
        if (!guestPin) {
          // Cleanup storage file
          await supabase.storage.from('wedding-photos').remove([storagePath])
          return {
            success: false,
            error: 'PIN ist erforderlich für den Upload.',
          }
        }

        const { data: photoIdResult, error: dbError } = await supabase.rpc('insert_guest_photo', {
          p_guest_id: guestId,
          p_storage_path: storagePath,
          p_filename: file.name,
          p_file_size: file.size,
          p_mime_type: file.type,
          p_guest_pin: guestPin,
        })

        if (dbError || !photoIdResult) {
          console.error('Database insert error:', dbError)
          // Cleanup: delete uploaded file if DB insert fails
          await supabase.storage.from('wedding-photos').remove([storagePath])
          return {
            success: false,
            error: dbError?.message?.includes('Invalid')
              ? 'Ungültiger PIN oder nicht autorisiert.'
              : 'Fehler beim Speichern der Foto-Informationen.',
          }
        }

        // Get photo data for response
        const { data: photoData } = await supabase
          .from('wedding_photos')
          .select()
          .eq('storage_path', storagePath)
          .single()

        if (!photoData) {
          console.error('Failed to retrieve inserted photo')
          return {
            success: false,
            error: 'Fehler beim Abrufen der Foto-Informationen.',
          }
        }

        // Generate thumbnail URL
        const { thumbnailUrl } = generatePhotoUrls(storagePath, supabase)

        return {
          success: true,
          photoId: photoData.id,
          thumbnailUrl,
        }
      } catch (error) {
        console.error('Upload error:', error)
        return {
          success: false,
          error: 'Ein unerwarteter Fehler ist aufgetreten.',
        }
      } finally {
        setUploading(false)
      }
    },
    [checkPhotoLimit]
  )

  /**
   * Fetch photos for a specific guest with pagination
   * @param guestId - ID of the guest
   * @param params - Query parameters for pagination and sorting
   * @returns Gallery response with photos and pagination info
   */
  const fetchGuestPhotos = useCallback(
    async (
      guestId: string,
      params: GalleryQueryParams,
      guestPin?: string
    ): Promise<GalleryResponse> => {
      try {
        setLoading(true)

        const { page, pageSize } = params
        const limit = pageSize === 'all' ? 1000 : pageSize
        const offset = pageSize === 'all' ? 0 : (page - 1) * pageSize

        // Use function-based query if PIN is provided, otherwise direct query
        let data: WeddingPhotoRow[] | null = null
        let count: number | null = null
        let error: any = null

        if (guestPin) {
          // Use function that validates PIN
          const { data: functionData, error: functionError } = await supabase.rpc(
            'get_guest_photos_with_pin',
            {
              p_guest_id: guestId,
              p_guest_pin: guestPin,
              p_limit: limit,
              p_offset: offset,
            }
          )

          if (functionError) {
            error = functionError
          } else {
            data = functionData as WeddingPhotoRow[]
            // Get total count - validate PIN by checking if guest exists with this PIN
            const { data: guestData } = await supabase
              .from('guests')
              .select('id')
              .eq('id', guestId)
              .eq('pin', guestPin)
              .single()

            if (guestData) {
              const { count: totalCount } = await supabase
                .from('wedding_photos')
                .select('*', { count: 'exact', head: true })
                .eq('guest_id', guestId)
              count = totalCount
            } else {
              count = 0
            }
          }
        } else {
          // Fallback to direct query (shouldn't happen in guest mode)
          const result = await supabase
            .from('wedding_photos')
            .select('*', { count: 'exact' })
            .eq('guest_id', guestId)
            .order('uploaded_at', { ascending: false })
            .range(offset, offset + limit - 1)

          data = result.data
          count = result.count
          error = result.error
        }

        if (error) {
          console.error('Error fetching photos:', error)
          return {
            photos: [],
            totalCount: 0,
            page,
            pageSize: typeof pageSize === 'number' ? pageSize : 0,
            totalPages: 0,
          }
        }

        const totalCount = count ?? 0
        const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / pageSize)

        // Transform to PhotoForDisplay
        const photos: PhotoForDisplay[] = (data || []).map((photo: WeddingPhotoRow) => {
          const { thumbnailUrl, fullSizeUrl } = generatePhotoUrls(photo.storage_path, supabase)
          return {
            id: photo.id,
            storagePath: photo.storage_path,
            thumbnailUrl,
            fullSizeUrl,
            filename: photo.filename,
            uploadedAt: new Date(photo.uploaded_at),
            fileSize: photo.file_size,
            canDelete: true, // Guest can delete own photos
          }
        })

        return {
          photos,
          totalCount,
          page,
          pageSize: typeof pageSize === 'number' ? pageSize : totalCount,
          totalPages,
        }
      } catch (error) {
        console.error('Error fetching guest photos:', error)
        return {
          photos: [],
          totalCount: 0,
          page: params.page,
          pageSize: typeof params.pageSize === 'number' ? params.pageSize : 0,
          totalPages: 0,
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Fetch all photos with guest information (admin only)
   * @param params - Query parameters for pagination, sorting, and filtering
   * @returns Gallery response with photos including guest names
   */
  const fetchAllPhotos = useCallback(
    async (params: GalleryQueryParams): Promise<GalleryResponse> => {
      try {
        setLoading(true)

        const { page, pageSize, guestId } = params
        const limit = pageSize === 'all' ? 1000 : pageSize
        const offset = pageSize === 'all' ? 0 : (page - 1) * pageSize

        // Build query
        let query = supabase
          .from('wedding_photos')
          .select(
            `
            *,
            guests!inner(primary_name, pin)
          `,
            { count: 'exact' }
          )
          .order('uploaded_at', { ascending: false })

        // Apply guest filter if provided
        if (guestId) {
          query = query.eq('guest_id', guestId)
        }

        // Apply pagination
        const { data, error, count } = await query.range(offset, offset + limit - 1)

        if (error) {
          console.error('Error fetching all photos:', error)
          return {
            photos: [],
            totalCount: 0,
            page,
            pageSize: typeof pageSize === 'number' ? pageSize : 0,
            totalPages: 0,
          }
        }

        const totalCount = count ?? 0
        const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / pageSize)

        // Transform to PhotoForDisplay with guest names
        const photos: PhotoForDisplay[] = (data || []).map((photo: any) => {
          const { thumbnailUrl, fullSizeUrl } = generatePhotoUrls(photo.storage_path, supabase)
          return {
            id: photo.id,
            storagePath: photo.storage_path,
            thumbnailUrl,
            fullSizeUrl,
            filename: photo.filename,
            uploadedAt: new Date(photo.uploaded_at),
            fileSize: photo.file_size,
            canDelete: true, // Admin can delete any photo
            guestName: photo.guests?.primary_name,
          }
        })

        return {
          photos,
          totalCount,
          page,
          pageSize: typeof pageSize === 'number' ? pageSize : totalCount,
          totalPages,
        }
      } catch (error) {
        console.error('Error fetching all photos:', error)
        return {
          photos: [],
          totalCount: 0,
          page: params.page,
          pageSize: typeof params.pageSize === 'number' ? params.pageSize : 0,
          totalPages: 0,
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  /**
   * Delete a photo (guest can delete own photos, admin can delete any)
   * @param photoId - ID of the photo to delete
   * @param storagePath - Storage path of the photo
   * @param guestPin - PIN of the guest (required for RLS policy if guest)
   * @returns Success status
   */
  const deletePhoto = useCallback(
    async (photoId: string, storagePath: string, guestPin?: string): Promise<boolean> => {
      try {
        // Delete from database using function if PIN provided (guest), otherwise direct delete (admin)
        let dbError: any = null

        if (guestPin) {
          // Use function that validates PIN
          const { error: functionError } = await supabase.rpc('delete_guest_photo', {
            p_photo_id: photoId,
            p_guest_pin: guestPin,
          })
          dbError = functionError
        } else {
          // Admin delete (no PIN needed)
          const { error: deleteError } = await supabase
            .from('wedding_photos')
            .delete()
            .eq('id', photoId)
          dbError = deleteError
        }

        if (dbError) {
          console.error('Database delete error:', dbError)
          return false
        }

        // Delete from storage
        const { error: storageError } = await supabase.storage
          .from('wedding-photos')
          .remove([storagePath])

        if (storageError) {
          console.error('Storage delete error:', storageError)
          // Note: DB record already deleted, log for cleanup
        }

        return true
      } catch (error) {
        console.error('Delete error:', error)
        return false
      }
    },
    []
  )

  return {
    uploading,
    loading,
    uploadPhoto,
    checkPhotoLimit,
    fetchGuestPhotos,
    fetchAllPhotos,
    deletePhoto,
  }
}
